import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { TrustLevelBadge, TrustScoreCard, VerificationChip, LEVELS } from '../components/TrustLevelBadge';
import { CheckCircle2, Circle, Clock, XCircle, Mail, Phone, User as UserIcon, Building2, Award, Crown, ArrowRight, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

export default function VerificationDashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openKyc, setOpenKyc] = useState(false);
  const [openKyb, setOpenKyb] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);
  const [mobileCode, setMobileCode] = useState('');
  const [mobileNum, setMobileNum] = useState('');
  const [mobileSent, setMobileSent] = useState(false);
  const [mockMobileCode, setMockMobileCode] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/verify/me');
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const sendEmailVerify = async () => {
    try {
      const res = await api.post('/verify/email/send');
      if (res.data.mock_token_for_beta) {
        toast.success('Beta mode: click the link below (email service not configured)', { duration: 10000 });
        // Auto-confirm since we're in beta mock mode
        await api.get(`/verify/email/confirm/${res.data.mock_token_for_beta}`);
        toast.success('Email verified (beta)');
        await fetchData();
        if (refresh) await refresh();
      } else {
        toast.success('Verification email sent — check your inbox');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    }
  };

  const sendMobileCode = async () => {
    try {
      const res = await api.post('/verify/mobile/send', { mobile: mobileNum });
      setMobileSent(true);
      if (res.data.mock_code_for_beta) {
        setMockMobileCode(res.data.mock_code_for_beta);
        toast.info(`Beta code: ${res.data.mock_code_for_beta}`);
      } else {
        toast.success('SMS sent');
      }
    } catch (e) {
      toast.error('Failed to send code');
    }
  };

  const confirmMobileCode = async () => {
    try {
      await api.post('/verify/mobile/confirm', { code: mobileCode });
      toast.success('Mobile verified');
      setOpenMobile(false);
      setMobileCode(''); setMobileNum(''); setMobileSent(false); setMockMobileCode('');
      await fetchData();
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Incorrect code');
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-12 text-center text-gray-500 font-mono text-sm">Loading verification…</div>
      </div>
    );
  }

  const { verification: v, level_info, reputation, next_steps } = data;
  const level = v.trust_level;
  const score = v.trust_score;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-5 h-5 text-[#0055FF]" />
            <span className="text-xs uppercase tracking-wider text-gray-500 font-mono">Trust Centre</span>
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Trust Centre</h1>
          <p className="text-sm text-gray-600 mt-1">Verify identity, business, source and quality — everything that makes an opportunity trustworthy.</p>
        </div>

        {/* Level ladder */}
        <div className="mb-8 border border-gray-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-5 divide-x divide-gray-200">
            {[0, 1, 2, 3, 4].map(l => {
              const cfg = LEVELS[l];
              const attained = level >= l;
              const Icon = cfg.icon;
              return (
                <div key={l} className={`p-4 flex flex-col items-center text-center ${attained ? 'bg-white' : 'bg-gray-50 opacity-60'}`}>
                  <div className={`w-10 h-10 rounded-full ${cfg.bg} ${cfg.text} border ${cfg.border} flex items-center justify-center mb-2`}>
                    <Icon className="w-5 h-5" strokeWidth={2.5} />
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-mono">Level {l}</div>
                  <div className="text-xs font-semibold text-gray-900 mt-0.5">{cfg.label}</div>
                  {attained && l === level && (
                    <div className="mt-2 text-[10px] px-1.5 py-0.5 bg-[#0055FF] text-white rounded font-mono uppercase tracking-wider">Current</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Score + stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <TrustScoreCard score={score} level={level} />
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <span className="text-xs uppercase tracking-wider text-gray-500">Verifications</span>
            <div className="flex flex-wrap gap-1.5 mt-3">
              <VerificationChip verified={v.email_verified} label="Email" icon={Mail} />
              <VerificationChip verified={v.mobile_verified} label="Mobile" icon={Phone} />
              <VerificationChip verified={v.kyc_status === 'approved'} label="Identity" icon={UserIcon} />
              <VerificationChip verified={v.kyb_status === 'approved'} label="Business" icon={Building2} />
              <VerificationChip verified={v.premium_grant} label="Premium" icon={Award} />
              <VerificationChip verified={v.enterprise_grant} label="Enterprise" icon={Crown} />
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <span className="text-xs uppercase tracking-wider text-gray-500">Reputation</span>
            <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
              <div><div className="text-gray-500 text-xs">Transactions</div><div className="font-mono font-semibold">{reputation.transactions_completed || 0}</div></div>
              <div><div className="text-gray-500 text-xs">Disputes</div><div className="font-mono font-semibold">{reputation.total_disputes || 0}</div></div>
              <div className="col-span-2"><div className="text-gray-500 text-xs">Repeat partners</div><div className="font-mono font-semibold">{reputation.repeat_partners || 0}</div></div>
            </div>
          </div>
        </div>

        {/* Next steps */}
        <section className="mb-8">
          <h2 className="font-heading text-lg font-semibold text-gray-900 mb-3">Next steps</h2>
          <div className="space-y-2" data-testid="verification-next-steps">
            <StepRow
              done={v.email_verified}
              icon={Mail}
              title="Verify your email"
              subtitle="Required to access most marketplace features."
              cta={v.email_verified ? null : 'Send verification email'}
              onClick={sendEmailVerify}
              testid="step-email"
            />
            <StepRow
              done={v.mobile_verified}
              icon={Phone}
              title="Verify your mobile number"
              subtitle="AU mobile number required for Deal Room notifications and payout security."
              cta={v.mobile_verified ? null : 'Verify mobile'}
              onClick={() => setOpenMobile(true)}
              testid="step-mobile"
            />
            <StepRow
              done={v.kyc_status === 'approved'}
              pending={v.kyc_status === 'pending'}
              rejected={v.kyc_status === 'rejected'}
              icon={UserIcon}
              title="Identity Verification (KYC)"
              subtitle={
                v.kyc_status === 'pending' ? 'Your submission is in review — typically < 24 hours.' :
                v.kyc_status === 'rejected' ? `Rejected: ${v.kyc_rejection_reason || 'Please resubmit'}` :
                'Verify your legal identity to reach Level 1 and unlock listing rights.'
              }
              cta={v.kyc_status !== 'approved' && v.kyc_status !== 'pending' ? 'Start identity verification' : null}
              onClick={() => setOpenKyc(true)}
              testid="step-kyc"
            />
            <StepRow
              done={v.kyb_status === 'approved'}
              pending={v.kyb_status === 'pending'}
              rejected={v.kyb_status === 'rejected'}
              icon={Building2}
              title="Business Verification (KYB)"
              subtitle={
                v.kyb_status === 'pending' ? 'Your business is under review.' :
                v.kyb_status === 'rejected' ? `Rejected: ${v.kyb_rejection_reason || 'Please resubmit'}` :
                'Verify your Australian business (ABN + ASIC) to reach Level 2 and unlock premium features.'
              }
              cta={v.kyb_status !== 'approved' && v.kyb_status !== 'pending' ? 'Verify business' : null}
              onClick={() => setOpenKyb(true)}
              testid="step-kyb"
            />
          </div>
        </section>

        {/* Benefits */}
        <section className="mb-4">
          <h2 className="font-heading text-lg font-semibold text-gray-900 mb-3">Benefits by level</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <BenefitCard levelBadge={<TrustLevelBadge level={1} size="sm" />} items={['Purchase leads up to $500', 'Post buyer requests', 'Deal Room access']} />
            <BenefitCard levelBadge={<TrustLevelBadge level={2} size="sm" />} items={['List leads for sale', 'Purchase up to $10,000/txn', 'Priority listing eligibility', 'Faster payout SLA (48h)']} />
            <BenefitCard levelBadge={<TrustLevelBadge level={3} size="sm" />} items={['Featured listings', 'Bulk purchase (10+ leads)', 'Premium seller badge', 'Lower transaction fee (12.5%)']} />
            <BenefitCard levelBadge={<TrustLevelBadge level={4} size="sm" />} items={['Dedicated account manager', 'API delivery access', 'Monthly PO invoicing', 'Custom contract terms']} />
          </div>
        </section>
      </div>

      <KycDialog open={openKyc} setOpen={setOpenKyc} onSubmitted={fetchData} />
      <KybDialog open={openKyb} setOpen={setOpenKyb} onSubmitted={fetchData} />

      {/* Mobile verify dialog */}
      <Dialog open={openMobile} onOpenChange={setOpenMobile}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Verify your mobile number</DialogTitle>
            <DialogDescription>We'll text you a 6-digit code. (Beta: code is shown in the response.)</DialogDescription>
          </DialogHeader>
          {!mobileSent ? (
            <div className="space-y-3">
              <Label>Mobile number</Label>
              <Input value={mobileNum} onChange={e => setMobileNum(e.target.value)} placeholder="+61 4XX XXX XXX" data-testid="mobile-number-input" />
              <Button onClick={sendMobileCode} className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="send-mobile-code-btn">Send code</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {mockMobileCode && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded font-mono">
                  Beta mode — code: <b>{mockMobileCode}</b>
                </div>
              )}
              <Label>Enter code</Label>
              <Input value={mobileCode} onChange={e => setMobileCode(e.target.value)} placeholder="123456" maxLength={6} data-testid="mobile-code-input" />
              <Button onClick={confirmMobileCode} className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="confirm-mobile-code-btn">Verify code</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StepRow = ({ done, pending, rejected, icon: Icon, title, subtitle, cta, onClick, testid }) => {
  let StatusIcon = Circle;
  let statusColor = 'text-gray-300';
  let borderColor = 'border-gray-200';
  if (done) { StatusIcon = CheckCircle2; statusColor = 'text-green-600'; borderColor = 'border-green-200 bg-green-50'; }
  else if (pending) { StatusIcon = Clock; statusColor = 'text-amber-600'; borderColor = 'border-amber-200 bg-amber-50'; }
  else if (rejected) { StatusIcon = XCircle; statusColor = 'text-red-600'; borderColor = 'border-red-200 bg-red-50'; }

  return (
    <div className={`border rounded-lg p-4 flex items-center gap-4 ${borderColor}`} data-testid={testid}>
      <StatusIcon className={`w-5 h-5 flex-shrink-0 ${statusColor}`} />
      <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-gray-900">{title}</div>
        <div className="text-xs text-gray-600 mt-0.5">{subtitle}</div>
      </div>
      {cta && (
        <Button variant="outline" size="sm" onClick={onClick} className="flex-shrink-0" data-testid={`${testid}-cta`}>
          {cta} <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
  );
};

const BenefitCard = ({ levelBadge, items }) => (
  <div className="border border-gray-200 rounded-lg p-4 bg-white">
    <div className="mb-2">{levelBadge}</div>
    <ul className="space-y-1 text-xs text-gray-700">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-1.5">
          <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 mt-0.5" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const KycDialog = ({ open, setOpen, onSubmitted }) => {
  const [form, setForm] = useState({
    id_type: 'drivers_licence', id_number: '', full_name: '', date_of_birth: '',
    address_line1: '', address_line2: '', suburb: '', state: '', postcode: '',
    country: 'Australia', mobile: '',
  });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const submit = async () => {
    try {
      await api.post('/verify/kyc/submit', form);
      toast.success('Identity verification submitted — admin will review within 24 hours');
      setOpen(false);
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Identity Verification (KYC)</DialogTitle>
          <DialogDescription>Beta: submissions are reviewed manually by our team. Real Stripe Identity coming soon.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>ID Type</Label>
            <Select value={form.id_type} onValueChange={v => set('id_type', v)}>
              <SelectTrigger data-testid="kyc-id-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="drivers_licence">Driver's Licence</SelectItem>
                <SelectItem value="passport">Passport</SelectItem>
                <SelectItem value="medicare">Medicare Card</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>ID Number</Label><Input value={form.id_number} onChange={e => set('id_number', e.target.value)} data-testid="kyc-id-number" /></div>
          <div><Label>Full Legal Name</Label><Input value={form.full_name} onChange={e => set('full_name', e.target.value)} data-testid="kyc-full-name" /></div>
          <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} data-testid="kyc-dob" /></div>
          <div><Label>Address Line 1</Label><Input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} data-testid="kyc-addr1" /></div>
          <div><Label>Address Line 2 (optional)</Label><Input value={form.address_line2} onChange={e => set('address_line2', e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Suburb</Label><Input value={form.suburb} onChange={e => set('suburb', e.target.value)} data-testid="kyc-suburb" /></div>
            <div>
              <Label>State</Label>
              <Select value={form.state} onValueChange={v => set('state', v)}>
                <SelectTrigger data-testid="kyc-state"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{AU_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Postcode</Label><Input value={form.postcode} onChange={e => set('postcode', e.target.value)} data-testid="kyc-postcode" /></div>
          </div>
          <div><Label>Mobile</Label><Input value={form.mobile} onChange={e => set('mobile', e.target.value)} placeholder="+61 4XX XXX XXX" data-testid="kyc-mobile" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-[#0055FF] hover:bg-[#0044CC] text-white w-full" data-testid="kyc-submit-btn">Submit for review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KybDialog = ({ open, setOpen, onSubmitted }) => {
  const [form, setForm] = useState({
    abn: '', business_name: '', trading_name: '', business_email: '', business_phone: '',
    website: '', industry: '', role_in_business: 'director',
  });
  const [abrPreview, setAbrPreview] = useState(null);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const lookupAbn = async () => {
    if (!form.abn) return;
    try {
      const res = await api.get(`/verify/abn/${form.abn.replace(/\s/g, '')}`);
      setAbrPreview(res.data);
      if (res.data.abr_lookup?.company_name && !form.business_name) {
        set('business_name', res.data.abr_lookup.company_name);
      }
    } catch (e) { /* ignore */ }
  };

  const submit = async () => {
    try {
      await api.post('/verify/kyb/submit', form);
      toast.success('Business verification submitted');
      setOpen(false);
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Business Verification (KYB)</DialogTitle>
          <DialogDescription>We verify your ABN via the ABR and cross-check ASIC company data.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>ABN</Label>
            <div className="flex gap-2">
              <Input value={form.abn} onChange={e => set('abn', e.target.value)} onBlur={lookupAbn} placeholder="11 digits" data-testid="kyb-abn" />
              <Button type="button" variant="outline" onClick={lookupAbn} data-testid="kyb-lookup-btn">Lookup</Button>
            </div>
            {abrPreview && (
              <div className={`mt-2 p-2 rounded text-xs ${abrPreview.valid_format ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
                {abrPreview.valid_format ? (
                  <>✓ Valid checksum {abrPreview.abr_lookup?.company_name && ` — ${abrPreview.abr_lookup.company_name}`}</>
                ) : 'Invalid ABN checksum'}
              </div>
            )}
          </div>
          <div><Label>Business Name</Label><Input value={form.business_name} onChange={e => set('business_name', e.target.value)} data-testid="kyb-business-name" /></div>
          <div><Label>Trading Name (optional)</Label><Input value={form.trading_name} onChange={e => set('trading_name', e.target.value)} /></div>
          <div><Label>Business Email</Label><Input type="email" value={form.business_email} onChange={e => set('business_email', e.target.value)} data-testid="kyb-email" /></div>
          <div><Label>Business Phone</Label><Input value={form.business_phone} onChange={e => set('business_phone', e.target.value)} data-testid="kyb-phone" /></div>
          <div><Label>Website (optional)</Label><Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://" data-testid="kyb-website" /></div>
          <div><Label>Industry (optional)</Label><Input value={form.industry} onChange={e => set('industry', e.target.value)} placeholder="e.g. Finance, Solar" /></div>
          <div>
            <Label>Your role in the business</Label>
            <Select value={form.role_in_business} onValueChange={v => set('role_in_business', v)}>
              <SelectTrigger data-testid="kyb-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="director">Director</SelectItem>
                <SelectItem value="owner">Sole Trader / Owner</SelectItem>
                <SelectItem value="authorised_representative">Authorised Representative</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="bg-[#0055FF] hover:bg-[#0044CC] text-white w-full" data-testid="kyb-submit-btn">Submit for review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
